import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useWebSocket } from '../../providers/WebSocketProvider';
import { apiPost } from '../../hooks/useApi';
import { useAssignedInboundFilter } from '../../hooks/useAssignedInboundFilter';
import { formatPhone } from '../../../lib/phone-format';

/**
 * Notification record. fromPhone/toPhone power the stacked "name → name /
 * phone → phone" row layout for phone-related events; resolvedFromName /
 * resolvedToName fill in lazily as we look the numbers up. contactId is
 * carried whenever the event provides it so the row click can navigate
 * directly without re-resolving.
 */
interface NotificationItem {
  id: string;
  type: string;
  /** Single-line label for events that aren't phone-shaped (e.g. queue.updated). */
  label: string | null;
  /** Body text shown beneath the label/phones row (e.g. SMS body, "Voice event"). */
  body: string;
  timestamp: number;
  read: boolean;
  contactId: string | null;
  fromPhone: string | null;
  toPhone: string | null;
  /** "From" side display name — populated by the contact-search lookup. */
  resolvedFromName: string | null;
  /** "To" side display name — same lookup, populated when the to-number maps to a contact. */
  resolvedToName: string | null;
  /**
   * Owner of the contact at event time (msg.received only) — evaluated at
   * render so the assigned/team-wide toggle re-filters the list in place.
   */
  assignedRepId: string | null;
}

const MAX_NOTIFICATIONS = 50;

function fullName(first: string | null | undefined, last: string | null | undefined): string {
  const f = (first ?? '').trim();
  const l = (last ?? '').trim();
  const joined = `${f} ${l}`.trim();
  return joined;
}

function wsEventToNotification(msg: Record<string, unknown>): NotificationItem | null {
  const eventType = (msg.type || msg.event) as string | undefined;
  if (!eventType) return null;

  const d = (msg.data ?? msg) as Record<string, unknown>;
  const ts = Date.now();
  const id = `${eventType}-${ts}-${Math.random().toString(36).slice(2, 7)}`;

  // contact_id arrives pre-resolved on events apex-queue enriches
  // (msg.received / msg.sent / voicemail events). Call events come straight
  // from apex-voice and don't carry it, so those need the phone fallback.
  const contactId = (d.contact_id as string) || null;
  const fromField = ((d.from as string) || (d.from_number as string) || '').trim();
  const toField = ((d.to as string) || (d.to_number as string) || '').trim();

  if (eventType === 'msg.received') {
    return {
      id,
      type: eventType,
      label: null,
      body: (d.body as string) || 'New message',
      timestamp: ts,
      read: false,
      contactId,
      fromPhone: fromField || null,
      toPhone: toField || null,
      resolvedFromName: null,
      resolvedToName: null,
      assignedRepId: (d.assigned_rep_id as string) || null,
    };
  }

  // queue.updated fires on every queue data change (scoring, dispositions,
  // syncs) and carries nothing a rep can act on, so it's deliberately not
  // surfaced in the bell — it flooded the list and buried real message/call
  // notifications (issue #1468). Other components still subscribe to it for
  // live data refresh; only the notification is suppressed here.

  if (eventType === 'call.ringing' || eventType === 'call.answered' || eventType === 'call.ended') {
    const direction = d.direction === 'outbound' ? 'outbound' : 'inbound';
    // Outbound calls are rep-initiated — the ActiveCallWidget surfaces
    // their state. Suppressing them in the bell matches the msg.sent
    // treatment so rep-initiated activity never becomes a notification.
    if (direction === 'outbound') return null;
    const labels: Record<string, string> = {
      'call.ringing': 'Incoming call',
      'call.answered': 'Call connected',
      'call.ended': 'Call ended',
    };
    return {
      id,
      type: eventType,
      label: labels[eventType] ?? null,
      body: '',
      timestamp: ts,
      read: false,
      contactId,
      fromPhone: fromField || null,
      toPhone: toField || null,
      resolvedFromName: null,
      resolvedToName: null,
      assignedRepId: null,
    };
  }

  if (eventType === 'voicemail.received') {
    return {
      id,
      type: eventType,
      label: 'New voicemail',
      body: '',
      timestamp: ts,
      read: false,
      contactId,
      fromPhone: fromField || null,
      toPhone: toField || null,
      resolvedFromName: null,
      resolvedToName: null,
      assignedRepId: null,
    };
  }

  return null;
}

function formatTimeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

interface PhoneLookup {
  contactId: string | null;
  name: string | null;
}

/**
 * Resolve a phone number to a contact id + display name via the existing
 * search endpoint. Returns the first exact-or-prefix phone match. The
 * lookup feeds both the click-to-navigate flow (contactId) and the row
 * "name on top, phone on bottom" rendering (name).
 */
async function resolveContactByPhone(phone: string): Promise<PhoneLookup> {
  const q = phone.trim();
  if (!q) return { contactId: null, name: null };
  try {
    const res = await apiPost<{
      data: Array<{
        id: string;
        first_name?: string | null;
        last_name?: string | null;
        phone?: string | null;
      }>;
    }>('/api/contacts/search', {
      q,
      columns: ['first_name', 'last_name', 'phone'],
      limit: 1,
    });
    const first = res.data?.[0];
    if (!first) return { contactId: null, name: null };
    const name = fullName(first.first_name, first.last_name);
    return { contactId: first.id, name: name || null };
  } catch {
    return { contactId: null, name: null };
  }
}

/**
 * Stacked from→to display for phone-shaped notifications. Top sub-row holds
 * names (or the event label as a fallback when neither side has resolved
 * yet); bottom sub-row holds the formatted phone numbers. Each sub-row uses
 * its own arrow so the directionality stays parseable even when one side
 * doesn't resolve to a contact.
 */
function PhoneRows({ item }: { item: NotificationItem }) {
  const fromName = item.resolvedFromName ?? '';
  const toName = item.resolvedToName ?? '';
  // When neither side has a name yet, the top row would otherwise be empty —
  // surface the event label there instead so the row never reads as a blank
  // pre-resolution flash.
  const showLabelAsTop = !fromName && !toName && !!item.label;
  const fromPhoneDisplay = item.fromPhone ? formatPhone(item.fromPhone) : '';
  const toPhoneDisplay = item.toPhone ? formatPhone(item.toPhone) : '';
  return (
    <div className="space-y-0.5">
      {showLabelAsTop ? (
        <p className="text-[0.75rem] font-medium text-foreground leading-snug truncate">
          {item.label}
        </p>
      ) : (
        <div className="flex items-center gap-1.5 text-[0.75rem] font-medium text-foreground leading-snug">
          <span className="flex-1 min-w-0 truncate">{fromName || '—'}</span>
          <span className="text-muted-foreground flex-shrink-0">→</span>
          <span className="flex-1 min-w-0 truncate text-right">{toName || '—'}</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 text-[0.6875rem] text-muted-foreground leading-snug tabular-nums">
        <span className="flex-1 min-w-0 truncate">{fromPhoneDisplay || '—'}</span>
        <span className="flex-shrink-0">→</span>
        <span className="flex-1 min-w-0 truncate text-right">{toPhoneDisplay || '—'}</span>
      </div>
    </div>
  );
}

export function NotificationBell() {
  const { subscribe } = useWebSocket();
  const { teamWide, canToggle, setTeamWide, shouldNotify } = useAssignedInboundFilter();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  /**
   * Per-component lookup cache. Entries in flight hold the in-progress
   * promise so two rows arriving with the same phone don't double-fetch;
   * settled entries hold the resolved value for fast re-use.
   */
  const lookupCache = useRef<Map<string, PhoneLookup | Promise<PhoneLookup>>>(new Map());

  // Assigned-contact scoping (#1471, #1541) — inbound messages only; call
  // and voicemail notifications keep their existing delivery. Filtering at
  // render (not arrival) means flipping the team-wide toggle re-scopes the
  // whole list both directions instantly.
  const visible = notifications.filter(
    (n) => n.type !== 'msg.received' || shouldNotify(n.assignedRepId),
  );
  const unreadCount = visible.filter((n) => !n.read).length;

  /**
   * Look a phone up against the cache, fetching once per distinct phone.
   * Mutates the matching notifications in place when the lookup settles
   * so the row can flip from "+1..." to "First Last" without a refetch.
   */
  const lookupAndApply = useCallback(async (phone: string, side: 'from' | 'to') => {
    const cached = lookupCache.current.get(phone);
    let result: PhoneLookup;
    if (cached instanceof Promise) {
      result = await cached;
    } else if (cached) {
      result = cached;
    } else {
      const promise = resolveContactByPhone(phone);
      lookupCache.current.set(phone, promise);
      result = await promise;
      lookupCache.current.set(phone, result);
    }
    if (!result.name && !result.contactId) return;
    setNotifications((prev) =>
      prev.map((n) => {
        if (side === 'from' && n.fromPhone === phone) {
          return {
            ...n,
            resolvedFromName: result.name ?? n.resolvedFromName,
            // Promote a contactId only when one wasn't already carried.
            contactId: n.contactId ?? result.contactId,
          };
        }
        if (side === 'to' && n.toPhone === phone) {
          return { ...n, resolvedToName: result.name ?? n.resolvedToName };
        }
        return n;
      }),
    );
  }, []);

  useEffect(() => {
    return subscribe('*', (raw: unknown) => {
      const msg = raw as Record<string, unknown>;
      const item = wsEventToNotification(msg);
      if (!item) return;
      setNotifications((prev) => [item, ...prev].slice(0, MAX_NOTIFICATIONS));
      if (item.fromPhone) void lookupAndApply(item.fromPhone, 'from');
      if (item.toPhone) void lookupAndApply(item.toPhone, 'to');
    });
  }, [subscribe, lookupAndApply]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  /**
   * Row click handler — mark the entry read, resolve its contact id (either
   * carried on the payload or via a phone lookup on the from-side), and
   * navigate. Non-resolvable entries stay in place with no nav; the user
   * gets immediate read-state feedback either way so the click isn't a no-op.
   */
  const handleRowClick = useCallback(
    async (n: NotificationItem) => {
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));

      let contactId = n.contactId;
      if (!contactId && n.fromPhone) {
        setResolving(n.id);
        const lookup = await resolveContactByPhone(n.fromPhone);
        contactId = lookup.contactId;
        setResolving(null);
      }
      if (contactId) {
        setOpen(false);
        navigate(`/contacts/${contactId}`);
      }
    },
    [navigate],
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="p-2 hover:bg-secondary transition-colors relative text-muted-foreground hover:text-foreground rounded-full border border-transparent cursor-pointer"
        title="Notifications"
      >
        <Bell className="w-4 h-4 stroke-[1.5]" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-destructive text-white text-[0.5625rem] font-bold flex items-center justify-center border-2 border-card">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {unreadCount === 0 && visible.length === 0 && (
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-muted-foreground/40 rounded-full border border-card" />
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1.5 w-[360px] bg-card border border-border/80 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/40">
            <span className="text-[0.8125rem] font-semibold text-foreground">Notifications</span>
            <div className="flex items-center gap-3">
              {canToggle && (
                <label className="flex items-center gap-1.5 text-[0.6875rem] text-muted-foreground cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={teamWide}
                    onChange={(e) => setTeamWide(e.target.checked)}
                    className="w-3 h-3 accent-primary cursor-pointer"
                  />
                  Team notifications
                </label>
              )}
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[0.6875rem] text-primary hover:text-primary/80 font-medium cursor-pointer"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                <Bell className="w-8 h-8 stroke-[1] mb-2 opacity-40" />
                <span className="text-[0.8125rem]">No notifications yet</span>
                <span className="text-[0.6875rem] mt-0.5">
                  Events will appear here in real time
                </span>
              </div>
            ) : (
              visible.map((n) => {
                const navigable = !!n.contactId || !!n.fromPhone;
                const isResolving = resolving === n.id;
                const phoneShaped = !!(n.fromPhone || n.toPhone);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => void handleRowClick(n)}
                    disabled={!navigable || isResolving}
                    className={`w-full text-left px-4 py-2.5 border-b border-border/30 last:border-0 transition-colors ${
                      n.read ? '' : 'bg-primary/[0.03]'
                    } ${
                      navigable ? 'hover:bg-secondary/60 cursor-pointer' : 'cursor-default'
                    } ${isResolving ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      )}
                      <div className={`flex-1 min-w-0 ${n.read ? 'pl-3.5' : ''}`}>
                        {phoneShaped ? (
                          <PhoneRows item={n} />
                        ) : (
                          <p className="text-[0.75rem] font-medium text-foreground leading-snug truncate">
                            {n.label ?? ''}
                          </p>
                        )}
                        {n.body && (
                          <p className="text-[0.6875rem] text-muted-foreground leading-snug mt-0.5 truncate">
                            {n.body}
                          </p>
                        )}
                      </div>
                      <span className="text-[0.625rem] text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {formatTimeAgo(n.timestamp)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
